import { useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { BRANCH_APPROVAL_STATUSES, type BranchApprovalStatus, type BranchRequestDto, type BranchRequestPage } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { dateTime } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Select } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';

const STATUS_LABEL: Record<BranchApprovalStatus, string> = {
  pending: 'Waiting',
  approved: 'Approved',
  rejected: 'Turned down',
};

const STATUS_TONE: Record<BranchApprovalStatus, 'warning' | 'success' | 'destructive'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'destructive',
};

export const BRANCH_REQUEST_COUNT_KEY = ['branch-requests', 'count'] as const;

export function useBranchRequestCount(enabled: boolean) {
  return useQuery({
    queryKey: BRANCH_REQUEST_COUNT_KEY,
    queryFn: () => get<{ pending: number }>('/platform/branch-requests/count'),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: false,
  });
}

type Decision = { row: BranchRequestDto; kind: 'approve' | 'reject' };

export function BranchRequestsPage() {
  const queryClient = useQueryClient();
  const canDecide = useOwnerCapability('owner.branch_approve');
  const [status, setStatus] = useState<BranchApprovalStatus>('pending');
  const [deciding, setDeciding] = useState<Decision | null>(null);

  const requests = useInfiniteQuery({
    queryKey: ['branch-requests', 'list', status],
    queryFn: ({ pageParam }) => get<BranchRequestPage>(`/platform/branch-requests${qs({ status, cursor: pageParam ?? undefined, limit: 50 })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = requests.data?.pages.flatMap((page) => page.items);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['branch-requests'] });
    setDeciding(null);
  };

  return (
    <>
      <PageHeader
        title="Branch requests"
        description="New branches facilities opened on the cloud. Nobody can sign in to a branch until it is approved here."
      />
      <SearchFilterBar
        filters={
          <FilterField label="Status">
            <Select className="w-48" value={status} onChange={(event) => setStatus(event.target.value as BranchApprovalStatus)}>
              {BRANCH_APPROVAL_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {STATUS_LABEL[value]}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={status !== 'pending' ? () => setStatus('pending') : undefined}
      />
      <DataTable
        rows={rows}
        loading={requests.isLoading}
        refreshing={requests.isFetching}
        error={requests.error}
        rowKey={(row) => `${row.tenantId}:${row.id}`}
        empty={status === 'pending' ? 'No branch is waiting for you.' : 'Nothing here.'}
        columns={[
          {
            header: 'Facility',
            sort: (row) => row.tenantName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.tenantName}</p>
                <p className="font-mono text-xs text-muted-foreground">{row.tenantSlug}</p>
              </div>
            ),
          },
          {
            header: 'Branch',
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <p>
                  {row.name} <span className="ms-1 font-mono text-xs text-muted-foreground">{row.code}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {[row.parentCode ? `Under ${row.parentCode}` : 'Main branch', row.city, row.country].filter(Boolean).join(' · ')}
                </p>
                {row.categoryModules.length > 0 && <p className="text-xs text-muted-foreground">{row.categoryModules.join(' · ')}</p>}
              </div>
            ),
          },
          { header: 'Asked by', sort: (row) => row.requestedByName, cell: (row) => row.requestedByName ?? '—' },
          { header: 'Asked on', sort: (row) => row.requestedAt, cell: (row) => <span className="whitespace-nowrap">{dateTime(row.requestedAt)}</span> },
          {
            header: 'Status',
            sort: (row) => row.approvalStatus,
            cell: (row) => (
              <div>
                <Badge variant={STATUS_TONE[row.approvalStatus]}>{STATUS_LABEL[row.approvalStatus]}</Badge>
                {row.rejectionReason && <p className="mt-1 max-w-xs text-xs text-muted-foreground">{row.rejectionReason}</p>}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              canDecide && row.approvalStatus === 'pending' ? (
                <div className="flex justify-end gap-2">
                  <Button size="sm" onClick={() => setDeciding({ row, kind: 'approve' })}>
                    Approve
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => setDeciding({ row, kind: 'reject' })}>
                    Turn down
                  </Button>
                </div>
              ) : null,
          },
        ]}
        footer={
          requests.hasNextPage && (
            <Button variant="outline" size="sm" onClick={() => void requests.fetchNextPage()} loading={requests.isFetchingNextPage}>
              Older requests
            </Button>
          )
        }
      />

      {deciding && (
        <OwnerConfirmDialog
          open
          onClose={() => setDeciding(null)}
          title={`${deciding.kind === 'approve' ? 'Approve' : 'Turn down'} ${deciding.row.name} at ${deciding.row.tenantName}`}
          description={
            deciding.kind === 'approve'
              ? 'Its staff can sign in to it straight away.'
              : 'The facility Super Admin sees the reason you give, can fix the branch and send it again.'
          }
          confirmLabel={deciding.kind === 'approve' ? 'Approve' : 'Turn down'}
          destructive={deciding.kind === 'reject'}
          successMessage={deciding.kind === 'approve' ? 'Branch approved' : 'Branch turned down'}
          action={(payload) => post(`/platform/branch-requests/${deciding.row.tenantId}/${deciding.row.id}/${deciding.kind}`, payload)}
          onDone={refresh}
        />
      )}
    </>
  );
}
